import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Layout/Navbar';
import './RaceTimeCalculator.css';

interface RaceDistance {
  name: string;
  miles: number;
  km: number;
}

interface RaceInput {
  id: string;
  distance: string;
  hours: string;
  minutes: string;
  seconds: string;
  elevation: string;
}

interface PredictionResult {
  distance: string;
  predictions: number[]; // predicted seconds from each input
  fastest: string;
  slowest: string;
  isSingle: boolean;
  fastestPaceMile: string;
  slowestPaceMile: string;
  fastestPaceKm: string;
  slowestPaceKm: string;
}

const DISTANCES: RaceDistance[] = [
  { name: '1 Mile', miles: 1, km: 1.609 },
  { name: '5K', miles: 3.107, km: 5 },
  { name: '10K', miles: 6.214, km: 10 },
  { name: 'Half Marathon', miles: 13.109, km: 21.0975 },
  { name: 'Marathon', miles: 26.219, km: 42.195 }
];

const makeInput = (id: string): RaceInput => ({
  id,
  distance: '5K',
  hours: '0',
  minutes: '25',
  seconds: '0',
  elevation: '0',
});

const RaceTimeCalculator: React.FC = () => {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState<RaceInput[]>([makeInput('A')]);
  const [targetElevations, setTargetElevations] = useState<{ [key: string]: string }>({
    '1 Mile': '0', '5K': '0', '10K': '0', 'Half Marathon': '0', 'Marathon': '0'
  });
  const [results, setResults] = useState<PredictionResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  const formatTime = (totalSeconds: number): string => {
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = Math.floor(totalSeconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPace = (secondsPerUnit: number): string => {
    const mins = Math.floor(secondsPerUnit / 60);
    const secs = Math.floor(secondsPerUnit % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const adjustForElevation = (timeInSeconds: number, distanceMiles: number, elevationFeet: number): number => {
    const elevationPerMile = elevationFeet / distanceMiles;
    const penaltyPerMile = (elevationPerMile / 100) * 30;
    return timeInSeconds + penaltyPerMile * distanceMiles;
  };

  const removeElevationEffect = (timeInSeconds: number, distanceMiles: number, elevationFeet: number): number => {
    const elevationPerMile = elevationFeet / distanceMiles;
    const penaltyPerMile = (elevationPerMile / 100) * 30;
    return timeInSeconds - penaltyPerMile * distanceMiles;
  };

  const predictRaceTime = (baseTimeSeconds: number, baseDistanceMiles: number, targetDistanceMiles: number): number => {
    return baseTimeSeconds * Math.pow(targetDistanceMiles / baseDistanceMiles, 1.06);
  };

  const updateInput = (id: string, field: keyof RaceInput, value: string) => {
    setInputs(prev => prev.map(inp => inp.id === id ? { ...inp, [field]: value } : inp));
  };

  const addInput = () => {
    if (inputs.length >= 3) return;
    const label = inputs.length === 1 ? 'B' : 'C';
    setInputs(prev => [...prev, makeInput(label)]);
  };

  const removeInput = (id: string) => {
    if (inputs.length <= 1) return;
    setInputs(prev => prev.filter(inp => inp.id !== id));
  };

  const handleCalculate = () => {
    // Validate all inputs have a time
    const validInputs = inputs.filter(inp => {
      const total = (parseInt(inp.hours) || 0) * 3600 + (parseInt(inp.minutes) || 0) * 60 + (parseInt(inp.seconds) || 0);
      return total > 0;
    });

    if (validInputs.length === 0) {
      alert('Please enter a valid time for at least one race');
      return;
    }

    const predictions: PredictionResult[] = DISTANCES.map(targetDist => {
      const targetElevFeet = parseFloat(targetElevations[targetDist.name]) || 0;

      const times = validInputs.map(inp => {
        const totalSec = (parseInt(inp.hours) || 0) * 3600 + (parseInt(inp.minutes) || 0) * 60 + (parseInt(inp.seconds) || 0);
        const inputDist = DISTANCES.find(d => d.name === inp.distance)!;
        const inputElevFeet = parseFloat(inp.elevation) || 0;

        // Flatten input time, predict, then apply target elevation
        const flatTime = removeElevationEffect(totalSec, inputDist.miles, inputElevFeet);
        const predictedFlat = predictRaceTime(flatTime, inputDist.miles, targetDist.miles);
        return adjustForElevation(predictedFlat, targetDist.miles, targetElevFeet);
      });

      const fastest = Math.min(...times);
      const slowest = Math.max(...times);

      return {
        distance: targetDist.name,
        predictions: times,
        fastest: formatTime(fastest),
        slowest: formatTime(slowest),
        isSingle: times.length === 1,
        fastestPaceMile: formatPace(fastest / targetDist.miles),
        slowestPaceMile: formatPace(slowest / targetDist.miles),
        fastestPaceKm: formatPace(fastest / targetDist.km),
        slowestPaceKm: formatPace(slowest / targetDist.km),
      };
    });

    setResults(predictions);
    setShowResults(true);
  };

  const handleElevationChange = (distance: string, value: string) => {
    setTargetElevations(prev => ({ ...prev, [distance]: value }));
  };

  const inputLabels = ['Race A (required)', 'Race B (optional)', 'Race C (optional)'];

  return (
    <div className="calculator-page">
      <Navbar />
      <div className="calculator-container">
        <div className="calculator-header">
          <h1>Race Time Predictor</h1>
          <p className="subtitle">
            Enter up to 3 recent races for a more accurate prediction range
          </p>
        </div>

        <div className="calculator-content">
          <div className="input-section">
            {inputs.map((inp, idx) => (
              <div key={inp.id} className="race-input-card">
                <div className="race-input-header">
                  <h2>{inputLabels[idx]}</h2>
                  {idx > 0 && (
                    <button className="btn-remove-input" onClick={() => removeInput(inp.id)}>
                      Remove
                    </button>
                  )}
                </div>

                <div className="form-group">
                  <label>Distance</label>
                  <select
                    value={inp.distance}
                    onChange={(e) => updateInput(inp.id, 'distance', e.target.value)}
                    className="form-control"
                  >
                    {DISTANCES.map(d => (
                      <option key={d.name} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Your Time</label>
                  <div className="time-inputs">
                    <input
                      type="number"
                      min="0"
                      placeholder="HH"
                      value={inp.hours}
                      onChange={(e) => updateInput(inp.id, 'hours', e.target.value)}
                      className="time-input"
                    />
                    <span>:</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      placeholder="MM"
                      value={inp.minutes}
                      onChange={(e) => updateInput(inp.id, 'minutes', e.target.value)}
                      className="time-input"
                    />
                    <span>:</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      placeholder="SS"
                      value={inp.seconds}
                      onChange={(e) => updateInput(inp.id, 'seconds', e.target.value)}
                      className="time-input"
                    />
                  </div>
                  <small>Hours : Minutes : Seconds</small>
                </div>

                <div className="form-group">
                  <label>Elevation Gain (feet)</label>
                  <input
                    type="number"
                    min="0"
                    value={inp.elevation}
                    onChange={(e) => updateInput(inp.id, 'elevation', e.target.value)}
                    className="form-control"
                    placeholder="0"
                  />
                  <small>Total elevation gain for this race</small>
                </div>
              </div>
            ))}

            {inputs.length < 3 && (
              <button onClick={addInput} className="btn-add-input">
                + Add Another Race
              </button>
            )}

            <button onClick={handleCalculate} className="btn-calculate">
              Calculate Predicted Times
            </button>
          </div>

          {showResults && (
            <div className="results-section">
              <h2>Your Predicted Race Times</h2>
              <p className="results-subtitle">
                {inputs.length > 1
                  ? `Based on ${inputs.length} race performances — showing your predicted range:`
                  : `Based on your ${inputs[0].distance} performance:`}
              </p>

              <div className="results-grid">
                {results.map((result, idx) => {
                  const isInput = inputs.some(inp => inp.distance === result.distance);
                  return (
                    <div key={idx} className={`result-card ${isInput ? 'current' : ''}`}>
                      <div className="result-distance">
                        {result.distance}
                        {isInput && <span className="badge">Input Race</span>}
                      </div>

                      {result.isSingle ? (
                        <>
                          <div className="result-time">{result.fastest}</div>
                          <div className="result-paces">
                            <div className="pace-item">
                              <span className="pace-label">Per Mile:</span>
                              <span className="pace-value">{result.fastestPaceMile}</span>
                            </div>
                            <div className="pace-item">
                              <span className="pace-label">Per Km:</span>
                              <span className="pace-value">{result.fastestPaceKm}</span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="result-range">
                            <div className="range-bound">
                              <span className="range-label">Fastest</span>
                              <span className="range-time">{result.fastest}</span>
                              <span className="range-pace">{result.fastestPaceMile}/mi</span>
                            </div>
                            <div className="range-divider">—</div>
                            <div className="range-bound">
                              <span className="range-label">Slowest</span>
                              <span className="range-time">{result.slowest}</span>
                              <span className="range-pace">{result.slowestPaceMile}/mi</span>
                            </div>
                          </div>
                          <div className="result-paces-range">
                            <div className="pace-item">
                              <span className="pace-label">Per Km:</span>
                              <span className="pace-value">{result.fastestPaceKm} – {result.slowestPaceKm}</span>
                            </div>
                          </div>
                        </>
                      )}

                      <div className="elevation-input-small">
                        <label>Target Elevation (ft):</label>
                        <input
                          type="number"
                          min="0"
                          value={targetElevations[result.distance]}
                          onChange={(e) => handleElevationChange(result.distance, e.target.value)}
                          className="form-control-small"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <button onClick={handleCalculate} className="btn-recalculate">
                Recalculate with Updated Elevations
              </button>

              <div className="cta-section">
                <div className="cta-card">
                  <h3>Want to achieve these times?</h3>
                  <p>
                    Get a personalized, AI-powered training plan that adapts to your progress and helps you reach your goals.
                  </p>
                  <button onClick={() => navigate('/')} className="btn-cta">
                    Start Training with Strava
                  </button>
                </div>
              </div>

              <div className="disclaimer-section">
                <p><strong>Note:</strong> These predictions are estimates based on Riegel's formula and standard elevation adjustments. Using multiple race inputs provides a range that accounts for your speed-endurance profile. Actual performance depends on training, weather, course profile, and race-day conditions.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RaceTimeCalculator;
